import { LogApi } from "../../Types/Generated/LogbookApi/LogApi"
import { IStoredCalculationSource } from "../../Types/Generated/LogbookModel/StoredCalculation"
import { entity, type Fold, type Identified } from "./Connection"
import { compareLogs } from "../LogOrder"

// A stored calc row, read structurally for its fold-relevant fields.
type StoredCalc = Identified & { version?: number; calculated?: unknown; calculatedV2?: unknown };

// The freshest calc for a source: highest version, breaking ties by id. There is
// realistically 0 or 1 row per (sourceId, sourceType), but this stays correct if a
// stale duplicate lingers.
function latestCalc(calcs: readonly Identified[]): StoredCalc | undefined {
  let best: StoredCalc | undefined;
  for (const calc of calcs) {
    const c = calc as StoredCalc;
    if (
      !best ||
      (c.version ?? 0) > (best.version ?? 0) ||
      ((c.version ?? 0) === (best.version ?? 0) && c.id > best.id)
    ) {
      best = c;
    }
  }
  return best;
}

// The two opt-in calc folds Log exposes: both read StoredCalculation rows indexed
// by `sourceId` and narrowed to log-sourced calcs. `calculated` projects the v1
// flat + its version; `calculatedV2` attaches the raw v2 sections. Each returns the
// base log unchanged when the relevant shape is absent (stability).
const logCalcFolds: Readonly<Record<string, Fold>> = {
  calculated: {
    source: "StoredCalculation",
    via: "sourceId",
    where: { sourceType: IStoredCalculationSource.log },
    apply: (log, calcs) => {
      const latest = latestCalc(calcs);
      return latest && latest.calculated !== undefined
        ? { ...log, calculated: latest.calculated, calculationVersion: latest.version }
        : log;
    },
  },
  calculatedV2: {
    source: "StoredCalculation",
    via: "sourceId",
    where: { sourceType: IStoredCalculationSource.log },
    apply: (log, calcs) => {
      const latest = latestCalc(calcs);
      return latest && latest.calculatedV2 !== undefined
        ? { ...log, calculatedV2: latest.calculatedV2 }
        : log;
    },
  },
};

// Log is the entity #1105 (data-layer core, tracker #665) proves end-to-end: a typed `logs.list` fetcher that returns
// a side-normalizing bundle (logs + statuses + calcs), a `timeUtc` sort, and two
// opt-in calc folds. Statuses and StoredCalculations that ride the bundle are
// upserted into their own stores (see Status / StoredCalculation connections).
export const LogEntity = entity("Log", {
  sort: compareLogs,
  fetch: (client, ids) => {
    const { shipId, dayId } = ids;
    if (shipId === undefined || dayId === undefined) {
      return Promise.reject(new Error("Log fetch requires shipId and dayId"));
    }
    return new LogApi(client).logs.list({ params: { shipId, dayId } }).call().then((r) => ({
      entities: {
        Log: r.logs ?? [],
        Status: r.statuses ?? [],
        // The rich logs.list response already carries calcs in the normalized
        // `IStoredLogCalculation` shape (real id, sourceId, version, ungated v1
        // `calculated` + optional v2 sections) — normalize them into their own
        // StoredCalculation entities so an opt-in fold can stitch them back on.
        StoredCalculation: r.calculationsV2 ?? [],
      },
      extras: { auditLogEdits: r.auditLogEdits ?? [] },
    }));
  },
  folds: logCalcFolds,
});
